import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useParams, Redirect } from "react-router-dom";
import { Box, Flex, Text } from "rebass/styled-components";
import { useProEditAccess } from "MVVM/Hooks/useProEditAccess";
import styled, { css } from "styled-components";
import {
  FaSkull,
  FaCrosshairs,
  FaPlus,
  FaMinus,
  FaTrash,
  FaUser,
  FaVideo,
  FaChevronUp,
  FaChevronDown,
  FaLayerGroup,
  FaCamera,
} from "react-icons/fa";

import {
  TimelineEditorData,
  TimelineEditorViewModel as useTimelineEditorViewModel,
} from "./ViewModel";

interface Selection {
  id: string;
  startTick: number;
  endTick: number;
  playerId: string;
  playerName: string;
  playerIds: string[];
  label: string;
}

interface RoundData {
  round: number;
  startTick: number;
  endTick: number;
  winner: string;
  terroristsScore: number;
  counterTerroristsScore: number;
  kills: KillEvent[];
  bomb: BombEvent[];
}

interface KillEvent {
  tick: number;
  killer: string;
  victim: string;
  location: string;
  killerData: {
    headShot: boolean;
    weapon: string;
    team: string;
  };
}

interface BombEvent {
  tick: number;
  type: string;
  site: string;
  initiator: string;
}

interface Player {
  name: string;
  steamId: string;
  observerSlot: number;
}

interface ReplayCommand {
  tick: number;
  class: string;
  command?: string;
  playerSlot?: number;
  gotoTick?: number;
  steamId64?: string;
}

interface TimelineEditorProps {
  data: TimelineEditorData;
  onGenerateSequence?: (sequence: ReplayCommand[]) => void;
  onSubmitSequence?: (
    sequence: ReplayCommand[],
    title?: string,
  ) => Promise<{
    success: boolean;
    errors: Array<{ field: string; code: string; message: string }>;
    moments: Array<{ id: string }>;
  }>;
  isSubmitting?: boolean;
}

const BUFFER_TICKS = 256;

type MountedCameraType =
  | "none"
  | "as_cam_face"
  | "as_cam_profile"
  | "as_cam_gun"
  | "as_cam_hip"
  | "as_cam_tps"
  | "as_cam_heli";

const MOUNTED_CAMERA_OPTIONS: {
  value: MountedCameraType;
  label: string;
  description: string;
}[] = [
  { value: "none", label: "None", description: "No camera effect" },
  { value: "as_cam_face", label: "Face", description: "Portrait angle" },
  { value: "as_cam_profile", label: "Profile", description: "Side view" },
  { value: "as_cam_gun", label: "Gun", description: "Weapon close-up" },
  { value: "as_cam_hip", label: "Hip", description: "Hip-level shot" },
  { value: "as_cam_tps", label: "Third Person", description: "Behind player" },
  { value: "as_cam_heli", label: "Aerial", description: "Helicopter view" },
];

const TimelineEditor = ({
  data,
  onGenerateSequence,
  onSubmitSequence,
  isSubmitting,
}: TimelineEditorProps) => {
  if (!data || !data.header) {
    return null;
  }

  const TICK_RATE = data.header.tickRate;
  const TOTAL_TICKS = data.header.totalTicks;
  const ROUNDS = data.rounds as RoundData[];
  const PLAYERS = data.players as Player[];

  const tickToTime = useCallback(
    (tick: number): string => {
      const seconds = tick / TICK_RATE;
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    },
    [TICK_RATE],
  );

  const getPlayerName = useCallback(
    (steamId: string): string => {
      const player = PLAYERS.find((p) => p.steamId === steamId);
      if (!player) return "Unknown";
      return player.name.replace(/^[⑧⑨⑩⑪⑫⑬]\s*[✓★☆]*\s*/, "").trim();
    },
    [PLAYERS],
  );

  const getPlayerObserverSlot = useCallback(
    (steamId: string): number => {
      const player = PLAYERS.find((p) => p.steamId === steamId);
      return player?.observerSlot || 0;
    },
    [PLAYERS],
  );

  const getRoundForTick = useCallback(
    (tick: number): RoundData | null => {
      return (
        ROUNDS.find(
          (r) => tick >= r.startTick && tick <= r.endTick && r.round !== 999,
        ) || null
      );
    },
    [ROUNDS],
  );

  const getKillsInRange = useCallback(
    (startTick: number, endTick: number, playerId?: string): KillEvent[] => {
      const kills: KillEvent[] = [];
      ROUNDS.forEach((round) => {
        round.kills?.forEach((kill) => {
          if (kill.tick >= startTick && kill.tick <= endTick) {
            if (!playerId || kill.killer === playerId) {
              kills.push(kill);
            }
          }
        });
      });
      return kills;
    },
    [ROUNDS],
  );

  const timeToTick = useCallback(
    (timeStr: string): number | null => {
      const match = timeStr.match(/^(\d+):(\d{2})$/);
      if (!match) return null;
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);
      if (seconds >= 60) return null;
      return (minutes * 60 + seconds) * TICK_RATE;
    },
    [TICK_RATE],
  );

  const getPlayerTeamInRound = useCallback(
    (steamId: string, round: RoundData): "ct" | "t" | null => {
      const isCT = (team: string) =>
        team === "CounterTerrorists" ||
        team === "CT" ||
        team?.toLowerCase().includes("counter");

      const getTeamFromKill = (
        kill: KillEvent,
        playerId: string,
      ): "ct" | "t" => {
        const killerIsCT = isCT(kill.killerData?.team);
        if (kill.killer === playerId) {
          return killerIsCT ? "ct" : "t";
        }
        return killerIsCT ? "t" : "ct";
      };

      const kill = round.kills?.find(
        (k) =>
          (k.killer === steamId || k.victim === steamId) &&
          k.killer !== "0" &&
          k.victim !== "0",
      );

      if (kill) {
        return getTeamFromKill(kill, steamId);
      }

      const sameHalfRounds = ROUNDS.filter(
        (r) =>
          (round.round <= 12 && r.round <= 12) ||
          (round.round > 12 && r.round > 12),
      );

      for (const r of sameHalfRounds) {
        const anyKill = r.kills?.find(
          (k) =>
            (k.killer === steamId || k.victim === steamId) &&
            k.killer !== "0" &&
            k.victim !== "0",
        );
        if (anyKill) {
          return getTeamFromKill(anyKill, steamId);
        }
      }

      return null;
    },
    [ROUNDS],
  );

  const [zoom, setZoom] = useState(2);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [currentSelection, setCurrentSelection] = useState<{
    startTick: number;
    endTick: number;
    playerId: string;
  } | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<{
    type: string;
    data: KillEvent | BombEvent;
    x: number;
    y: number;
  } | null>(null);
  const [defaultLeadUp, setDefaultLeadUp] = useState(5);
  const [defaultAftermath, setDefaultAftermath] = useState(5);
  const [toast, setToast] = useState<string | null>(null);
  const [weaponFilter, setWeaponFilter] = useState<string>("all");
  const [killPovPrefs, setKillPovPrefs] = useState<
    Record<string, "killer" | "victim">
  >({});
  const [mountedCameras, setMountedCameras] = useState<
    Record<string, MountedCameraType>
  >({});
  const [clipTitle, setClipTitle] = useState<string>("");
  const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);

  const COOLDOWN_SECONDS = 60;

  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const validRounds = useMemo(
    () => ROUNDS.filter((r) => r.round !== 999),
    [ROUNDS],
  );

  const uniqueWeapons = useMemo(() => {
    const weapons = new Set<string>();
    validRounds.forEach((round) => {
      round.kills?.forEach((kill) => {
        if (kill.killerData?.weapon) {
          weapons.add(kill.killerData.weapon);
        }
      });
    });
    return Array.from(weapons).sort();
  }, [validRounds]);

  const firstRoundStart = validRounds[0]?.startTick || 0;
  const lastRoundEnd =
    validRounds[validRounds.length - 1]?.endTick || TOTAL_TICKS;
  const matchDuration = lastRoundEnd - firstRoundStart;

  const baseWidth = 2000;
  const timelineWidth = baseWidth * zoom;

  const playerStats = useMemo(() => {
    const stats: Record<string, { kills: number; deaths: number }> = {};
    PLAYERS.forEach((p) => {
      stats[p.steamId] = { kills: 0, deaths: 0 };
    });
    validRounds.forEach((round) => {
      round.kills?.forEach((kill) => {
        if (stats[kill.killer]) stats[kill.killer].kills++;
        if (stats[kill.victim]) stats[kill.victim].deaths++;
      });
    });
    return stats;
  }, [PLAYERS, validRounds]);

  const sortedPlayers = useMemo(() => {
    const getPlayerTeam = (steamId: string): "ct" | "t" | null => {
      for (const round of validRounds) {
        const kill = round.kills?.find(
          (k) =>
            (k.killer === steamId || k.victim === steamId) &&
            k.killer !== "0" &&
            k.victim !== "0",
        );
        if (kill) {
          const isCT = (team: string) =>
            team === "CounterTerrorists" ||
            team === "CT" ||
            team?.toLowerCase().includes("counter");
          const killerIsCT = isCT(kill.killerData?.team);
          if (kill.killer === steamId) {
            return killerIsCT ? "ct" : "t";
          }
          return killerIsCT ? "t" : "ct";
        }
      }
      return null;
    };

    const ctPlayers: Player[] = [];
    const tPlayers: Player[] = [];
    const unknownPlayers: Player[] = [];

    PLAYERS.forEach((player) => {
      const team = getPlayerTeam(player.steamId);
      if (team === "ct") {
        ctPlayers.push(player);
      } else if (team === "t") {
        tPlayers.push(player);
      } else {
        unknownPlayers.push(player);
      }
    });

    return [...ctPlayers, ...tPlayers, ...unknownPlayers];
  }, [PLAYERS, validRounds]);

  const sortedSelections = useMemo(
    () => [...selections].sort((a, b) => a.startTick - b.startTick),
    [selections],
  );

  const tickToPixel = useCallback(
    (tick: number): number => {
      const relativeTick = tick - firstRoundStart;
      return (relativeTick / matchDuration) * timelineWidth;
    },
    [firstRoundStart, matchDuration, timelineWidth],
  );

  const pixelToTick = useCallback(
    (pixel: number): number => {
      const relativeTick = (pixel / timelineWidth) * matchDuration;
      return Math.round(relativeTick + firstRoundStart);
    },
    [firstRoundStart, matchDuration, timelineWidth],
  );

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    if (!cooldownEndTime) {
      setCooldownRemaining(0);
      return;
    }

    const updateCooldown = () => {
      const remaining = Math.ceil((cooldownEndTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setCooldownEndTime(null);
        setCooldownRemaining(0);
      } else {
        setCooldownRemaining(remaining);
      }
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [cooldownEndTime]);

  const PLAYER_INFO_WIDTH = 200;

  const handleTimelineMouseDown = (e: React.MouseEvent, playerId: string) => {
    if (!timelineRef.current || !playerId) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollPosition - PLAYER_INFO_WIDTH;
    const tick = pixelToTick(x);

    setIsSelecting(true);
    setSelectionStart(tick);
    setCurrentSelection({
      startTick: tick,
      endTick: tick,
      playerId,
    });
  };

  const handleTimelineMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !timelineRef.current || selectionStart === null) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollPosition - PLAYER_INFO_WIDTH;
    const tick = pixelToTick(x);

    setCurrentSelection((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        startTick: Math.min(selectionStart, tick),
        endTick: Math.max(selectionStart, tick),
      };
    });
  };

  const handleTimelineMouseUp = () => {
    if (
      currentSelection &&
      currentSelection.endTick > currentSelection.startTick
    ) {
      const playerName = getPlayerName(currentSelection.playerId);
      setSelections((prev) => [
        ...prev,
        {
          id: `selection-${Date.now()}`,
          startTick: currentSelection.startTick,
          endTick: currentSelection.endTick,
          playerId: currentSelection.playerId,
          playerIds: [currentSelection.playerId],
          playerName,
          label: `${playerName} - ${tickToTime(currentSelection.startTick)}`,
        },
      ]);
    }
    setIsSelecting(false);
    setSelectionStart(null);
    setCurrentSelection(null);
  };

  const handleDeleteSelection = (id: string) => {
    setSelections((prev) => prev.filter((s) => s.id !== id));
  };

  const canAdjustTime = (
    selection: Selection,
    field: "startTick" | "endTick",
    delta: number,
  ): boolean => {
    const newValue =
      field === "startTick"
        ? selection.startTick + delta
        : selection.endTick + delta;

    if (field === "startTick") {
      if (newValue >= selection.endTick) return false;
      if (newValue < firstRoundStart) return false;
      const duration = selection.endTick - newValue;
      if (duration < TICK_RATE) return false;
    } else {
      if (newValue <= selection.startTick) return false;
      if (newValue > lastRoundEnd) return false;
      const duration = newValue - selection.startTick;
      if (duration < TICK_RATE) return false;
    }

    return true;
  };

  const handleUpdateSelectionTime = (
    id: string,
    field: "startTick" | "endTick",
    value: number,
  ) => {
    setSelections((prev) => {
      const selection = prev.find((s) => s.id === id);
      if (!selection) return prev;

      const updated = { ...selection, [field]: value };
      if (updated.startTick >= updated.endTick) return prev;

      const duration = updated.endTick - updated.startTick;
      if (duration < TICK_RATE) return prev;

      const overlapping = prev.find(
        (s) =>
          s.id !== id &&
          updated.startTick < s.endTick &&
          updated.endTick > s.startTick,
      );

      if (overlapping) {
        const combinedPlayerIds = [
          ...new Set([
            ...(selection.playerIds || [selection.playerId]),
            ...(overlapping.playerIds || [overlapping.playerId]),
          ]),
        ];
        const mergedPlayerNames = combinedPlayerIds
          .map((pid) => getPlayerName(pid))
          .join(", ");
        const merged: Selection = {
          id: `merged-${Date.now()}`,
          startTick: Math.min(updated.startTick, overlapping.startTick),
          endTick: Math.max(updated.endTick, overlapping.endTick),
          playerId: selection.playerId,
          playerIds: combinedPlayerIds,
          playerName: mergedPlayerNames,
          label: `Merged clip (${combinedPlayerIds.length} players)`,
        };
        setToast("Clips merged automatically");
        return prev
          .filter((s) => s.id !== id && s.id !== overlapping.id)
          .concat(merged);
      }

      const kills = getKillsInRange(
        updated.startTick,
        updated.endTick,
        selection.playerId,
      );

      if (kills.length > 0) {
        const firstKill = kills[0];
        const newLeadUp = Math.round(
          (firstKill.tick - updated.startTick) / TICK_RATE,
        );
        const newAftermath = Math.round(
          (updated.endTick - firstKill.tick) / TICK_RATE,
        );

        if (field === "startTick" && newLeadUp > 0) {
          setDefaultLeadUp(newLeadUp);
        } else if (field === "endTick" && newAftermath > 0) {
          setDefaultAftermath(newAftermath);
        }
      }

      return prev.map((s) => (s.id === id ? updated : s));
    });
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  const scrollToRound = (round: RoundData) => {
    if (containerRef.current) {
      const scrollX = tickToPixel(round.startTick);
      containerRef.current.scrollTo({
        left: scrollX,
        behavior: "smooth",
      });
    }
  };

  const addKillMoment = (kill: KillEvent, playerId: string) => {
    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    const playerName = getPlayerName(playerId);
    const newStart = Math.max(kill.tick - leadUpTicks, firstRoundStart);
    const newEnd = Math.min(kill.tick + aftermathTicks, lastRoundEnd);

    setSelections((prev) => {
      const overlapping = prev.find(
        (s) => newStart < s.endTick && newEnd > s.startTick,
      );

      if (overlapping) {
        const mergedPlayerIds = overlapping.playerIds.includes(playerId)
          ? overlapping.playerIds
          : [...overlapping.playerIds, playerId];
        const mergedPlayerNames = mergedPlayerIds
          .map((id) => getPlayerName(id))
          .join(", ");
        const merged: Selection = {
          id: `merged-${Date.now()}`,
          startTick: Math.min(newStart, overlapping.startTick),
          endTick: Math.max(newEnd, overlapping.endTick),
          playerId: overlapping.playerId,
          playerIds: mergedPlayerIds,
          playerName: mergedPlayerNames,
          label: `Merged clip (${mergedPlayerIds.length} players)`,
        };
        setToast("Clips merged automatically");
        return prev.filter((s) => s.id !== overlapping.id).concat(merged);
      }

      return [
        ...prev,
        {
          id: `kill-${kill.tick}-${Date.now()}`,
          startTick: newStart,
          endTick: newEnd,
          playerId,
          playerIds: [playerId],
          playerName,
          label: `${playerName} - Kill at ${tickToTime(kill.tick)}`,
        },
      ];
    });
  };

  const addDeathMoment = (kill: KillEvent, victimId: string) => {
    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    const playerName = getPlayerName(victimId);
    const newStart = Math.max(kill.tick - leadUpTicks, firstRoundStart);
    const newEnd = Math.min(kill.tick + aftermathTicks, lastRoundEnd);

    const newSelectionId = `death-${kill.tick}-${Date.now()}`;
    const povKey = `${newSelectionId}-${kill.tick}`;

    setSelections((prev) => {
      const overlapping = prev.find(
        (s) => newStart < s.endTick && newEnd > s.startTick,
      );

      if (overlapping) {
        const mergedPlayerIds = overlapping.playerIds.includes(victimId)
          ? overlapping.playerIds
          : [...overlapping.playerIds, victimId];
        const mergedPlayerNames = mergedPlayerIds
          .map((id) => getPlayerName(id))
          .join(", ");
        const merged: Selection = {
          id: `merged-${Date.now()}`,
          startTick: Math.min(newStart, overlapping.startTick),
          endTick: Math.max(newEnd, overlapping.endTick),
          playerId: overlapping.playerId,
          playerIds: mergedPlayerIds,
          playerName: mergedPlayerNames,
          label: `Merged clip (${mergedPlayerIds.length} players)`,
        };
        setToast("Clips merged automatically");
        return prev.filter((s) => s.id !== overlapping.id).concat(merged);
      }

      setKillPovPrefs((p) => ({
        ...p,
        [povKey]: "victim",
      }));

      return [
        ...prev,
        {
          id: newSelectionId,
          startTick: newStart,
          endTick: newEnd,
          playerId: victimId,
          playerIds: [victimId],
          playerName,
          label: `${playerName} - Death at ${tickToTime(kill.tick)}`,
        },
      ];
    });
  };

  const getPlayerKillsInRound = (steamId: string, round: RoundData) => {
    const kills = round.kills?.filter((k) => k.killer === steamId) || [];
    if (weaponFilter === "all") return kills;
    return kills.filter((k) => k.killerData?.weapon === weaponFilter);
  };

  const getPlayerDeathsInRound = (steamId: string, round: RoundData) => {
    return round.kills?.filter((k) => k.victim === steamId) || [];
  };

  const getKillsInRangeAsVictim = useCallback(
    (startTick: number, endTick: number, playerId: string): KillEvent[] => {
      const kills: KillEvent[] = [];
      ROUNDS.forEach((round) => {
        round.kills?.forEach((kill) => {
          if (kill.tick >= startTick && kill.tick <= endTick) {
            if (kill.victim === playerId) {
              kills.push(kill);
            }
          }
        });
      });
      return kills;
    },
    [ROUNDS],
  );

  const getKillPovPlayer = (selection: Selection, kill: KillEvent): string => {
    const povKey = `${selection.id}-${kill.tick}`;
    const playerIds = selection.playerIds || [selection.playerId];
    const isVictimKill = playerIds.includes(kill.victim);
    const isKillerKill = playerIds.includes(kill.killer);
    const defaultPov = isVictimKill && !isKillerKill ? "victim" : "killer";
    const pov = killPovPrefs[povKey] || defaultPov;
    return pov === "killer" ? kill.killer : kill.victim;
  };

  const generateReplaySequence = (): ReplayCommand[] => {
    const sequence: ReplayCommand[] = [];
    let lastTick = 0;

    sequence.push({
      tick: 0,
      class: "execCommand",
      command: "cl_draw_only_deathnotices 1",
    });

    sortedSelections.forEach((selection) => {
      const playerIds = selection.playerIds || [selection.playerId];

      const allKillsSet = new Set<string>();
      const allKillsList: KillEvent[] = [];

      playerIds.forEach((pid) => {
        const killsAsKiller = getKillsInRange(
          selection.startTick,
          selection.endTick,
          pid,
        );
        const killsAsVictim = getKillsInRangeAsVictim(
          selection.startTick,
          selection.endTick,
          pid,
        );
        [...killsAsKiller, ...killsAsVictim].forEach((kill) => {
          const killKey = `${kill.tick}-${kill.killer}-${kill.victim}`;
          if (!allKillsSet.has(killKey)) {
            allKillsSet.add(killKey);
            allKillsList.push(kill);
          }
        });
      });

      const allKills = allKillsList.sort((a, b) => a.tick - b.tick);

      const initialPovPlayer =
        allKills.length > 0
          ? getKillPovPlayer(selection, allKills[0])
          : selection.playerId;
      const playerSlot = getPlayerObserverSlot(initialPovPlayer);
      const gotoTick = selection.startTick - BUFFER_TICKS;

      sequence.push({
        tick: lastTick,
        class: "gotoTickAndSpectatePlayer",
        playerSlot,
        gotoTick,
        steamId64: initialPovPlayer,
      });

      let currentPovPlayer = initialPovPlayer;
      let currentRecordStart = selection.startTick;
      let hasAddedMountedCamSegment = false;
      let recordingStarted = false;

      const povSwitches: { tick: number; steamId64: string }[] = [];

      allKills.forEach((kill, index) => {
        const killPovPlayer = getKillPovPlayer(selection, kill);

        if (index > 0 && killPovPlayer !== currentPovPlayer) {
          const switchTick = Math.max(
            allKills[index - 1].tick + TICK_RATE,
            kill.tick - TICK_RATE * 2,
          );
          povSwitches.push({
            tick: switchTick,
            steamId64: killPovPlayer,
          });
          currentPovPlayer = killPovPlayer;
        }
      });

      currentPovPlayer = initialPovPlayer;

      allKills.forEach((kill) => {
        const camKey = `${selection.id}-${kill.tick}-cam`;
        const mountedCam = mountedCameras[camKey];
        const killPovPlayer = getKillPovPlayer(selection, kill);

        const povSwitchesBeforeKill = povSwitches.filter(
          (ps) =>
            ps.tick >= currentRecordStart &&
            ps.tick < kill.tick - 7 &&
            ps.steamId64 === killPovPlayer,
        );

        if (mountedCam && mountedCam !== "none") {
          if (!recordingStarted) {
            sequence.push({
              tick: currentRecordStart,
              class: "startRecord",
            });
            recordingStarted = true;
          }

          povSwitchesBeforeKill.forEach((ps) => {
            sequence.push({
              tick: ps.tick,
              class: "spectatePlayer",
              steamId64: ps.steamId64,
            });
          });

          if (!hasAddedMountedCamSegment) {
            sequence.push({
              tick: kill.tick - 7,
              class: "endRecord",
            });
            hasAddedMountedCamSegment = true;
            recordingStarted = false;
          }

          sequence.push({
            tick: kill.tick - 7,
            class: "execCommand",
            command: mountedCam,
          });

          sequence.push({
            tick: kill.tick - 2,
            class: "startRecord",
          });
          recordingStarted = true;

          const camEndTick = Math.min(
            kill.tick + TICK_RATE * 2,
            selection.endTick - 2,
          );
          sequence.push({
            tick: camEndTick,
            class: "endRecord",
          });
          recordingStarted = false;

          sequence.push({
            tick: camEndTick + 1,
            class: "execCommand",
            command: "as_cam_off",
          });

          currentRecordStart = camEndTick + 2;
        } else {
          povSwitchesBeforeKill.forEach((ps) => {
            if (!recordingStarted) {
              sequence.push({
                tick: currentRecordStart,
                class: "startRecord",
              });
              recordingStarted = true;
            }
            sequence.push({
              tick: ps.tick,
              class: "spectatePlayer",
              steamId64: ps.steamId64,
            });
          });
        }
      });

      const remainingPovSwitches = povSwitches.filter(
        (ps) => ps.tick >= currentRecordStart && ps.tick < selection.endTick,
      );

      if (!recordingStarted && currentRecordStart < selection.endTick) {
        sequence.push({
          tick: currentRecordStart,
          class: "startRecord",
        });
        recordingStarted = true;
      }

      remainingPovSwitches.forEach((ps) => {
        if (
          !sequence.some(
            (cmd) =>
              cmd.class === "spectatePlayer" &&
              cmd.tick === ps.tick &&
              cmd.steamId64 === ps.steamId64,
          )
        ) {
          sequence.push({
            tick: ps.tick,
            class: "spectatePlayer",
            steamId64: ps.steamId64,
          });
        }
      });

      if (recordingStarted) {
        sequence.push({
          tick: selection.endTick,
          class: "endRecord",
        });
      } else if (currentRecordStart < selection.endTick) {
        sequence.push({
          tick: currentRecordStart,
          class: "startRecord",
        });
        sequence.push({
          tick: selection.endTick,
          class: "endRecord",
        });
      }

      lastTick = selection.endTick + 64;
    });

    return sequence;
  };

  const handleCreateVideo = async () => {
    const sequence = generateReplaySequence();

    if (onGenerateSequence) {
      onGenerateSequence(sequence);
    }

    if (onSubmitSequence) {
      const result = await onSubmitSequence(
        sequence,
        clipTitle.trim() || undefined,
      );
      if (result.success) {
        setToast("Clip creation started!");
        setCooldownEndTime(Date.now() + COOLDOWN_SECONDS * 1000);
      } else {
        setToast(result.errors[0]?.message || "Failed to create clip");
      }
    }
  };

  const renderTooltip = () => {
    if (!hoveredEvent) return null;

    let content;
    if (hoveredEvent.type === "kill") {
      const kill = hoveredEvent.data as KillEvent;
      content = (
        <>
          <TooltipTitle>
            {kill.killerData?.headShot ? "Headshot Kill" : "Kill"}
          </TooltipTitle>
          <TooltipRow>
            <span>Killer:</span> {getPlayerName(kill.killer)}
          </TooltipRow>
          <TooltipRow>
            <span>Victim:</span> {getPlayerName(kill.victim)}
          </TooltipRow>
          <TooltipRow>
            <span>Weapon:</span> {kill.killerData?.weapon}
          </TooltipRow>
          <TooltipRow>
            <span>Location:</span> {kill.location}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(kill.tick)}
          </TooltipRow>
          <TooltipAction>Click to add this moment</TooltipAction>
        </>
      );
    } else if (hoveredEvent.type === "death") {
      const death = hoveredEvent.data as KillEvent;
      content = (
        <>
          <TooltipTitle>Death</TooltipTitle>
          <TooltipRow>
            <span>Killed by:</span> {getPlayerName(death.killer)}
          </TooltipRow>
          <TooltipRow>
            <span>Weapon:</span> {death.killerData?.weapon}
          </TooltipRow>
          <TooltipRow>
            <span>Location:</span> {death.location}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(death.tick)}
          </TooltipRow>
          <TooltipAction>Click to add from victim POV</TooltipAction>
        </>
      );
    } else if (hoveredEvent.type === "bomb") {
      const bomb = hoveredEvent.data as BombEvent;
      content = (
        <>
          <TooltipTitle>
            {bomb.type === "PLANTED" ? "Bomb Planted" : "Bomb Defused"}
          </TooltipTitle>
          <TooltipRow>
            <span>Site:</span> {bomb.site}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(bomb.tick)}
          </TooltipRow>
        </>
      );
    }

    return (
      <Tooltip style={{ left: hoveredEvent.x + 10, top: hoveredEvent.y - 10 }}>
        {content}
      </Tooltip>
    );
  };

  return (
    <Container>
      <Header>
        <Flex alignItems="center" style={{ gap: "16px" }}>
          <MapBadge>
            <Text fontSize="14px" fontWeight="600" color="#71AAFF">
              {data.header.mapName.replace("de_", "").toUpperCase()}
            </Text>
          </MapBadge>
          <Text fontSize="18px" fontWeight="500" color="#f7fcfd">
            Create Your Highlight Video
          </Text>
        </Flex>
        <Flex alignItems="center" style={{ gap: "12px" }}>
          <Text fontSize="14px" color="#8fa9af">
            Match Duration: {tickToTime(matchDuration)}
          </Text>
        </Flex>
      </Header>

      <MainContent>
        <TimelinePanel>
          <ControlBar>
            <Flex alignItems="center" style={{ gap: "12px" }}>
              <Text fontSize="14px" fontWeight="600" color="#f7fcfd">
                Rounds
              </Text>
              <RoundIndicator>
                {validRounds.map((round) => {
                  const isCTWin =
                    round.winner === "CounterTerrorists" ||
                    round.winner === "Counter Terrorists";
                  const showHalftime = round.round === 12;

                  return (
                    <React.Fragment key={round.round}>
                      <RoundPip
                        active={false}
                        isCTWin={isCTWin}
                        onClick={() => scrollToRound(round)}
                        title={`Round ${round.round} - ${
                          isCTWin ? "CT" : "T"
                        } Win (${round.counterTerroristsScore}-${
                          round.terroristsScore
                        })`}
                      >
                        <span className="round-num">{round.round}</span>
                      </RoundPip>
                      {showHalftime && <HalftimeDivider />}
                    </React.Fragment>
                  );
                })}
              </RoundIndicator>
            </Flex>
            <Flex alignItems="center" style={{ gap: "16px" }}>
              <Flex alignItems="center" style={{ gap: "8px" }}>
                <Text fontSize="12px" color="#557077">
                  Weapon:
                </Text>
                <WeaponSelect
                  value={weaponFilter}
                  onChange={(e) => setWeaponFilter(e.target.value)}
                >
                  <option value="all">All Weapons</option>
                  {uniqueWeapons.map((weapon) => (
                    <option key={weapon} value={weapon}>
                      {weapon}
                    </option>
                  ))}
                </WeaponSelect>
              </Flex>
              <Flex alignItems="center" style={{ gap: "8px" }}>
                <Text fontSize="12px" color="#557077">
                  Zoom:
                </Text>
                <ZoomButton
                  onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                >
                  <FaMinus size={10} />
                </ZoomButton>
                <Text
                  fontSize="12px"
                  color="#8fa9af"
                  style={{ minWidth: "40px", textAlign: "center" }}
                >
                  {Math.round(zoom * 100)}%
                </Text>
                <ZoomButton
                  onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                >
                  <FaPlus size={10} />
                </ZoomButton>
              </Flex>
            </Flex>
          </ControlBar>

          <TimelineContainer ref={containerRef} onScroll={handleScroll}>
            <TimelineInner
              ref={timelineRef}
              style={{ width: timelineWidth + 200 }}
            >
              <TimelineRuler>
                <RulerHeader>
                  <PlayerColumnHeader></PlayerColumnHeader>
                  <RulerRow>
                    {Array.from({
                      length: Math.ceil(matchDuration / TICK_RATE / 30) + 1,
                    }).map((_, i) => {
                      const tick = firstRoundStart + i * 30 * TICK_RATE;
                      if (tick > lastRoundEnd) return null;
                      return (
                        <RulerMark key={i} style={{ left: tickToPixel(tick) }}>
                          <span>{tickToTime(tick - firstRoundStart)}</span>
                        </RulerMark>
                      );
                    })}
                  </RulerRow>
                </RulerHeader>
                <RoundsHeader>
                  <PlayerColumnHeader></PlayerColumnHeader>
                  <RoundsRow>
                    {validRounds.map((round) => {
                      const startX = tickToPixel(round.startTick);
                      const width = tickToPixel(round.endTick) - startX;

                      return (
                        <RoundBlock
                          key={round.round}
                          style={{ left: startX, width }}
                          winner={round.winner}
                        >
                          <RoundLabel>
                            R{round.round}
                            <RoundScore>
                              <span className="ct">
                                {round.counterTerroristsScore}
                              </span>
                              -
                              <span className="t">{round.terroristsScore}</span>
                            </RoundScore>
                          </RoundLabel>
                        </RoundBlock>
                      );
                    })}
                  </RoundsRow>
                </RoundsHeader>
              </TimelineRuler>

              <PlayersContainer>
                {sortedPlayers.map((player) => {
                  const isSelected = selectedPlayer === player.steamId;
                  const stats = playerStats[player.steamId];

                  return (
                    <PlayerRow
                      key={player.steamId}
                      selected={isSelected}
                      onClick={() => setSelectedPlayer(player.steamId)}
                    >
                      <PlayerInfo selected={isSelected}>
                        <PlayerAvatar selected={isSelected}>
                          <FaUser size={12} />
                        </PlayerAvatar>
                        <PlayerDetails>
                          <PlayerName selected={isSelected}>
                            {getPlayerName(player.steamId)}
                          </PlayerName>
                          <PlayerStats>
                            <StatBadge type="kills">
                              <FaSkull size={8} /> {stats?.kills || 0}
                            </StatBadge>
                            <StatBadge type="deaths">
                              <FaCrosshairs size={8} /> {stats?.deaths || 0}
                            </StatBadge>
                          </PlayerStats>
                        </PlayerDetails>
                      </PlayerInfo>

                      <PlayerTimeline
                        selected={isSelected}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleTimelineMouseDown(e, player.steamId);
                        }}
                        onMouseMove={handleTimelineMouseMove}
                        onMouseUp={handleTimelineMouseUp}
                        onMouseLeave={handleTimelineMouseUp}
                      >
                        {validRounds.map((round) => {
                          const startX = tickToPixel(round.startTick);
                          const width = tickToPixel(round.endTick) - startX;
                          const team = getPlayerTeamInRound(
                            player.steamId,
                            round,
                          );

                          return (
                            <RoundSegment
                              key={`${player.steamId}-${round.round}`}
                              style={{ left: startX, width }}
                              team={team}
                              selected={isSelected}
                            />
                          );
                        })}

                        {validRounds.map((round) => {
                          const kills = getPlayerKillsInRound(
                            player.steamId,
                            round,
                          );
                          const deaths = getPlayerDeathsInRound(
                            player.steamId,
                            round,
                          );

                          return (
                            <React.Fragment
                              key={`events-${player.steamId}-${round.round}`}
                            >
                              {kills.map((kill) => (
                                <KillMarker
                                  key={`kill-${kill.tick}`}
                                  style={{ left: tickToPixel(kill.tick) }}
                                  isHeadshot={kill.killerData?.headShot}
                                  selected={isSelected}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addKillMoment(kill, player.steamId);
                                  }}
                                  onMouseEnter={(e) => {
                                    setHoveredEvent({
                                      type: "kill",
                                      data: kill,
                                      x: e.clientX,
                                      y: e.clientY,
                                    });
                                  }}
                                  onMouseLeave={() => setHoveredEvent(null)}
                                >
                                  {kill.killerData?.headShot ? (
                                    <FaCrosshairs size={12} />
                                  ) : (
                                    <FaSkull size={12} />
                                  )}
                                  {isSelected && (
                                    <KillLabel>
                                      {kill.killerData?.weapon}
                                    </KillLabel>
                                  )}
                                </KillMarker>
                              ))}
                              {deaths.map((death) => (
                                <DeathMarker
                                  key={`death-${death.tick}`}
                                  style={{ left: tickToPixel(death.tick) }}
                                  selected={isSelected}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addDeathMoment(death, player.steamId);
                                  }}
                                  onMouseEnter={(e) => {
                                    setHoveredEvent({
                                      type: "death",
                                      data: death,
                                      x: e.clientX,
                                      y: e.clientY,
                                    });
                                  }}
                                  onMouseLeave={() => setHoveredEvent(null)}
                                >
                                  <FaSkull size={10} />
                                </DeathMarker>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {selections
                          .filter((s) => s.playerId === player.steamId)
                          .map((selection) => {
                            const startX = tickToPixel(selection.startTick);
                            const width =
                              tickToPixel(selection.endTick) - startX;

                            return (
                              <SelectionOverlay
                                key={selection.id}
                                style={{ left: startX, width }}
                              >
                                <SelectionHandle className="left" />
                                <SelectionHandle className="right" />
                              </SelectionOverlay>
                            );
                          })}

                        {currentSelection &&
                          currentSelection.playerId === player.steamId && (
                            <SelectionOverlay
                              style={{
                                left: tickToPixel(currentSelection.startTick),
                                width:
                                  tickToPixel(currentSelection.endTick) -
                                  tickToPixel(currentSelection.startTick),
                              }}
                              isActive
                            />
                          )}
                      </PlayerTimeline>
                    </PlayerRow>
                  );
                })}
              </PlayersContainer>
            </TimelineInner>
          </TimelineContainer>
        </TimelinePanel>

        <SelectionsPanel>
          <PanelHeader>
            <Text fontSize="14px" fontWeight="600" color="#f7fcfd">
              Your Segments ({selections.length})
            </Text>
            <Text fontSize="12px" color="#557077">
              These will become your highlight video
            </Text>
          </PanelHeader>

          <SelectionsList>
            {selections.length === 0 ? (
              <EmptyState>
                <EmptyIcon>
                  <FaVideo size={24} />
                </EmptyIcon>
                <Text
                  fontSize="14px"
                  fontWeight="500"
                  color="#f7fcfd"
                  textAlign="center"
                >
                  No clips yet
                </Text>
                <Text fontSize="12px" color="#557077" textAlign="center">
                  Click on kill icons to quickly add moments, or drag on a
                  player's timeline to select custom ranges
                </Text>
              </EmptyState>
            ) : (
              sortedSelections.map((selection, index) => {
                const round = getRoundForTick(selection.startTick);
                const playerIds = selection.playerIds || [selection.playerId];

                const killsSet = new Set<string>();
                const killsList: KillEvent[] = [];
                const deathsSet = new Set<string>();
                const deathsList: KillEvent[] = [];

                playerIds.forEach((pid) => {
                  getKillsInRange(
                    selection.startTick,
                    selection.endTick,
                    pid,
                  ).forEach((kill) => {
                    const key = `${kill.tick}-${kill.killer}-${kill.victim}`;
                    if (!killsSet.has(key)) {
                      killsSet.add(key);
                      killsList.push(kill);
                    }
                  });
                  getKillsInRangeAsVictim(
                    selection.startTick,
                    selection.endTick,
                    pid,
                  ).forEach((death) => {
                    const key = `${death.tick}-${death.killer}-${death.victim}`;
                    if (!deathsSet.has(key)) {
                      deathsSet.add(key);
                      deathsList.push(death);
                    }
                  });
                });

                const kills = killsList.sort((a, b) => a.tick - b.tick);
                const deaths = deathsList.sort((a, b) => a.tick - b.tick);

                return (
                  <SelectionCard key={selection.id}>
                    <ClipHeader>
                      <ClipNumber>{index + 1}</ClipNumber>
                      <Flex alignItems="center" style={{ gap: "6px", flex: 1 }}>
                        <FaUser size={10} color="#71AAFF" />
                        <Text fontSize="13px" fontWeight="500" color="#f7fcfd">
                          {selection.playerName}
                        </Text>
                      </Flex>
                      <DeleteButton
                        onClick={() => handleDeleteSelection(selection.id)}
                      >
                        <FaTrash size={10} />
                      </DeleteButton>
                    </ClipHeader>

                    <ClipContext>
                      {round && (
                        <ContextBadge>
                          <span>Round {round.round}</span>
                        </ContextBadge>
                      )}
                      {kills.length > 0 && (
                        <ContextBadge type="kills">
                          <FaCrosshairs size={8} />
                          <span>
                            {kills.length} kill{kills.length > 1 ? "s" : ""}
                          </span>
                        </ContextBadge>
                      )}
                      {deaths.length > 0 && (
                        <ContextBadge type="deaths">
                          <FaSkull size={8} />
                          <span>
                            {deaths.length} death{deaths.length > 1 ? "s" : ""}
                          </span>
                        </ContextBadge>
                      )}
                      <ContextBadge type="duration">
                        {Math.round(
                          (selection.endTick - selection.startTick) / TICK_RATE,
                        )}
                        s
                      </ContextBadge>
                    </ClipContext>

                    {kills.length > 0 && (
                      <KillTimingSection>
                        <KillTimingHeader>Kills & POV</KillTimingHeader>
                        {kills.map((kill, killIndex) => {
                          const leadUp =
                            (kill.tick - selection.startTick) / TICK_RATE;
                          const aftermath =
                            (selection.endTick - kill.tick) / TICK_RATE;
                          const position =
                            ((kill.tick - selection.startTick) /
                              (selection.endTick - selection.startTick)) *
                            100;
                          const povKey = `${selection.id}-${kill.tick}`;
                          const camKey = `${selection.id}-${kill.tick}-cam`;
                          const currentPov = killPovPrefs[povKey] || "killer";
                          const currentCam = mountedCameras[camKey] || "none";

                          return (
                            <KillTimingRow key={`kill-timing-${killIndex}`}>
                              <KillTimingBar>
                                <KillTimingMarker
                                  style={{ left: `${position}%` }}
                                >
                                  <FaCrosshairs size={8} />
                                </KillTimingMarker>
                              </KillTimingBar>
                              <KillDetailsRow>
                                <KillTimingInfo>
                                  <span className="weapon">
                                    {kill.killerData?.weapon}
                                  </span>
                                  <span className="timing">
                                    {leadUp.toFixed(1)}s in /{" "}
                                    {aftermath.toFixed(1)}s out
                                  </span>
                                </KillTimingInfo>
                                <PovToggle>
                                  <PovOption
                                    active={currentPov === "killer"}
                                    onClick={() =>
                                      setKillPovPrefs((prev) => ({
                                        ...prev,
                                        [povKey]: "killer",
                                      }))
                                    }
                                  >
                                    <FaCrosshairs size={9} />
                                    <span>{getPlayerName(kill.killer)}</span>
                                  </PovOption>
                                  <PovOption
                                    active={currentPov === "victim"}
                                    onClick={() =>
                                      setKillPovPrefs((prev) => ({
                                        ...prev,
                                        [povKey]: "victim",
                                      }))
                                    }
                                  >
                                    <FaSkull size={9} />
                                    <span>{getPlayerName(kill.victim)}</span>
                                  </PovOption>
                                </PovToggle>
                              </KillDetailsRow>
                              <MountedCameraSection>
                                <MountedCameraHeader>
                                  <FaCamera size={10} />
                                  <span>Mounted Camera Effect</span>
                                </MountedCameraHeader>
                                <MountedCameraOptions>
                                  {MOUNTED_CAMERA_OPTIONS.map((option) => (
                                    <MountedCameraOption
                                      key={option.value}
                                      active={currentCam === option.value}
                                      onClick={() =>
                                        setMountedCameras((prev) => ({
                                          ...prev,
                                          [camKey]: option.value,
                                        }))
                                      }
                                    >
                                      <span className="label">
                                        {option.label}
                                      </span>
                                    </MountedCameraOption>
                                  ))}
                                </MountedCameraOptions>
                                {currentCam !== "none" && (
                                  <MountedCameraPreview>
                                    <span className="effect-label">
                                      Effect:{" "}
                                      {
                                        MOUNTED_CAMERA_OPTIONS.find(
                                          (o) => o.value === currentCam,
                                        )?.description
                                      }
                                    </span>
                                    <span className="duration">
                                      ~2s before kill
                                    </span>
                                  </MountedCameraPreview>
                                )}
                              </MountedCameraSection>
                            </KillTimingRow>
                          );
                        })}
                      </KillTimingSection>
                    )}

                    {deaths.length > 0 && kills.length === 0 && (
                      <KillTimingSection>
                        <KillTimingHeader>Deaths & POV</KillTimingHeader>
                        {deaths.map((death, deathIndex) => {
                          const leadUp =
                            (death.tick - selection.startTick) / TICK_RATE;
                          const aftermath =
                            (selection.endTick - death.tick) / TICK_RATE;
                          const position =
                            ((death.tick - selection.startTick) /
                              (selection.endTick - selection.startTick)) *
                            100;
                          const povKey = `${selection.id}-${death.tick}`;
                          const camKey = `${selection.id}-${death.tick}-cam`;
                          const currentPov = killPovPrefs[povKey] || "victim";
                          const currentCam = mountedCameras[camKey] || "none";

                          return (
                            <KillTimingRow key={`death-timing-${deathIndex}`}>
                              <DeathTimingBar>
                                <DeathTimingMarker
                                  style={{ left: `${position}%` }}
                                >
                                  <FaSkull size={8} />
                                </DeathTimingMarker>
                              </DeathTimingBar>
                              <KillDetailsRow>
                                <KillTimingInfo>
                                  <span className="weapon">
                                    {death.killerData?.weapon}
                                  </span>
                                  <span className="timing">
                                    {leadUp.toFixed(1)}s in /{" "}
                                    {aftermath.toFixed(1)}s out
                                  </span>
                                </KillTimingInfo>
                                <PovToggle>
                                  <PovOption
                                    active={currentPov === "killer"}
                                    onClick={() =>
                                      setKillPovPrefs((prev) => ({
                                        ...prev,
                                        [povKey]: "killer",
                                      }))
                                    }
                                  >
                                    <FaCrosshairs size={9} />
                                    <span>{getPlayerName(death.killer)}</span>
                                  </PovOption>
                                  <PovOption
                                    active={currentPov === "victim"}
                                    onClick={() =>
                                      setKillPovPrefs((prev) => ({
                                        ...prev,
                                        [povKey]: "victim",
                                      }))
                                    }
                                  >
                                    <FaSkull size={9} />
                                    <span>{getPlayerName(death.victim)}</span>
                                  </PovOption>
                                </PovToggle>
                              </KillDetailsRow>
                              <MountedCameraSection>
                                <MountedCameraHeader>
                                  <FaCamera size={10} />
                                  <span>Mounted Camera Effect</span>
                                </MountedCameraHeader>
                                <MountedCameraOptions>
                                  {MOUNTED_CAMERA_OPTIONS.map((option) => (
                                    <MountedCameraOption
                                      key={option.value}
                                      active={currentCam === option.value}
                                      onClick={() =>
                                        setMountedCameras((prev) => ({
                                          ...prev,
                                          [camKey]: option.value,
                                        }))
                                      }
                                    >
                                      <span className="label">
                                        {option.label}
                                      </span>
                                    </MountedCameraOption>
                                  ))}
                                </MountedCameraOptions>
                                {currentCam !== "none" && (
                                  <MountedCameraPreview>
                                    <span className="effect-label">
                                      Effect:{" "}
                                      {
                                        MOUNTED_CAMERA_OPTIONS.find(
                                          (o) => o.value === currentCam,
                                        )?.description
                                      }
                                    </span>
                                    <span className="duration">
                                      ~2s before death
                                    </span>
                                  </MountedCameraPreview>
                                )}
                              </MountedCameraSection>
                            </KillTimingRow>
                          );
                        })}
                      </KillTimingSection>
                    )}

                    <TimeAdjustRow>
                      <TimeAdjustGroup>
                        <TimeLabel>Start</TimeLabel>
                        <TimeInputWrapper>
                          <TimeIncrementBtn
                            disabled={
                              !canAdjustTime(selection, "startTick", -TICK_RATE)
                            }
                            onClick={() =>
                              handleUpdateSelectionTime(
                                selection.id,
                                "startTick",
                                selection.startTick - TICK_RATE,
                              )
                            }
                          >
                            <FaChevronUp size={8} />
                          </TimeIncrementBtn>
                          <TimeInput
                            key={`start-${selection.id}-${selection.startTick}`}
                            type="text"
                            defaultValue={tickToTime(
                              selection.startTick - firstRoundStart,
                            )}
                            onBlur={(e) => {
                              const tick = timeToTick(e.target.value);
                              if (tick !== null) {
                                handleUpdateSelectionTime(
                                  selection.id,
                                  "startTick",
                                  tick + firstRoundStart,
                                );
                              } else {
                                e.target.value = tickToTime(
                                  selection.startTick - firstRoundStart,
                                );
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                          />
                          <TimeIncrementBtn
                            disabled={
                              !canAdjustTime(selection, "startTick", TICK_RATE)
                            }
                            onClick={() =>
                              handleUpdateSelectionTime(
                                selection.id,
                                "startTick",
                                selection.startTick + TICK_RATE,
                              )
                            }
                          >
                            <FaChevronDown size={8} />
                          </TimeIncrementBtn>
                        </TimeInputWrapper>
                      </TimeAdjustGroup>
                      <TimeAdjustGroup>
                        <TimeLabel>End</TimeLabel>
                        <TimeInputWrapper>
                          <TimeIncrementBtn
                            disabled={
                              !canAdjustTime(selection, "endTick", -TICK_RATE)
                            }
                            onClick={() =>
                              handleUpdateSelectionTime(
                                selection.id,
                                "endTick",
                                selection.endTick - TICK_RATE,
                              )
                            }
                          >
                            <FaChevronUp size={8} />
                          </TimeIncrementBtn>
                          <TimeInput
                            key={`end-${selection.id}-${selection.endTick}`}
                            type="text"
                            defaultValue={tickToTime(
                              selection.endTick - firstRoundStart,
                            )}
                            onBlur={(e) => {
                              const tick = timeToTick(e.target.value);
                              if (tick !== null) {
                                handleUpdateSelectionTime(
                                  selection.id,
                                  "endTick",
                                  tick + firstRoundStart,
                                );
                              } else {
                                e.target.value = tickToTime(
                                  selection.endTick - firstRoundStart,
                                );
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                          />
                          <TimeIncrementBtn
                            disabled={
                              !canAdjustTime(selection, "endTick", TICK_RATE)
                            }
                            onClick={() =>
                              handleUpdateSelectionTime(
                                selection.id,
                                "endTick",
                                selection.endTick + TICK_RATE,
                              )
                            }
                          >
                            <FaChevronDown size={8} />
                          </TimeIncrementBtn>
                        </TimeInputWrapper>
                      </TimeAdjustGroup>
                    </TimeAdjustRow>
                  </SelectionCard>
                );
              })
            )}
          </SelectionsList>
          {selections.length > 0 && (
            <PanelFooter>
              <ClipTitleSection>
                <ClipTitleLabel>Clip Title</ClipTitleLabel>
                <ClipTitleInput
                  type="text"
                  placeholder="Enter a title for your clip..."
                  value={clipTitle}
                  onChange={(e) => setClipTitle(e.target.value)}
                  maxLength={100}
                />
              </ClipTitleSection>
              <TotalDuration>
                <Text fontSize="12px" color="#557077">
                  Total video length:
                </Text>
                <Text fontSize="14px" fontWeight="600" color="#4dbd94">
                  {Math.round(
                    selections.reduce(
                      (acc, s) => acc + (s.endTick - s.startTick),
                      0,
                    ) / TICK_RATE,
                  )}
                  s
                </Text>
              </TotalDuration>
              <CreateButton
                onClick={handleCreateVideo}
                disabled={isSubmitting || cooldownRemaining > 0}
              >
                <FaVideo size={12} />
                <span>
                  {isSubmitting
                    ? "Creating..."
                    : cooldownRemaining > 0
                    ? `Wait ${cooldownRemaining}s`
                    : "Create Highlight Video"}
                </span>
              </CreateButton>
            </PanelFooter>
          )}
        </SelectionsPanel>
      </MainContent>

      {renderTooltip()}

      {toast && (
        <Toast>
          <FaLayerGroup size={14} />
          <span>{toast}</span>
        </Toast>
      )}
    </Container>
  );
};

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  background: #0b1116;
  color: #f7fcfd;
  overflow: hidden;
`;

const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #131d25;
  border-bottom: 1px solid #1e2e3a;
`;

const MapBadge = styled(Box)`
  padding: 6px 12px;
  background: rgba(113, 170, 255, 0.1);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 6px;
`;

const MainContent = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;

const TimelinePanel = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0b1116;
  position: relative;
`;

const ControlBar = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #131d25;
  border-bottom: 1px solid #1e2e3a;
`;

const RoundIndicator = styled(Flex)`
  align-items: center;
  gap: 3px;
  padding: 0 12px;
  border-left: 1px solid #253a49;
  margin-left: 8px;
`;

const RoundPip = styled.button<{ active: boolean; isCTWin: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 32px;
  padding: 2px 4px;
  border: 2px solid
    ${({ active, isCTWin }) =>
      active ? (isCTWin ? "#71AAFF" : "#F95370") : "transparent"};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ active, isCTWin }) =>
    active
      ? isCTWin
        ? "rgba(113, 170, 255, 0.25)"
        : "rgba(249, 83, 112, 0.25)"
      : isCTWin
      ? "rgba(113, 170, 255, 0.1)"
      : "rgba(249, 83, 112, 0.1)"};

  .round-num {
    font-size: 10px;
    font-weight: 600;
    color: ${({ isCTWin }) => (isCTWin ? "#71AAFF" : "#F95370")};
    line-height: 1;
  }

  .kills-count {
    font-size: 8px;
    color: #557077;
    line-height: 1;
  }

  &:hover {
    background: ${({ isCTWin }) =>
      isCTWin ? "rgba(113, 170, 255, 0.3)" : "rgba(249, 83, 112, 0.3)"};
    transform: translateY(-2px);
  }
`;

const HalftimeDivider = styled.div`
  width: 2px;
  height: 24px;
  background: linear-gradient(to bottom, transparent, #557077, transparent);
  margin: 0 4px;
`;

const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #253a49;
    color: #f7fcfd;
  }
`;

const WeaponSelect = styled.select`
  padding: 6px 28px 6px 10px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238fa9af'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;

  &:hover {
    background-color: #253a49;
    border-color: #71aaff;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
  }

  option {
    background: #1a2933;
    color: #f7fcfd;
  }
`;

const TimelineContainer = styled(Box)`
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #0b1116;
  }
  &::-webkit-scrollbar-thumb {
    background: #253a49;
    border-radius: 4px;
  }
`;

const TimelineInner = styled(Box)`
  position: relative;
  min-height: 100%;
`;

const TimelineRuler = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #131d25;
`;

const RulerHeader = styled(Flex)`
  height: 24px;
`;

const RoundsHeader = styled(Flex)`
  height: 28px;
  background: #0f171d;
`;

const PlayerColumnHeader = styled(Flex)`
  position: sticky;
  left: 0;
  z-index: 15;
  align-items: center;
  width: 200px;
  min-width: 200px;
  padding: 0 12px;
  background: #131d25;
  font-size: 11px;
  font-weight: 600;
  color: #557077;
`;

const RulerRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
  border-bottom: 1px solid #1e2e3a;
`;

const RulerMark = styled(Box)`
  position: absolute;
  top: 0;
  height: 100%;
  border-left: 1px solid #253a49;

  span {
    position: absolute;
    top: 4px;
    left: 4px;
    font-size: 10px;
    color: #557077;
    white-space: nowrap;
  }
`;

const RoundsRow = styled(Box)`
  position: relative;
  flex: 1;
  height: 100%;
`;

const RoundBlock = styled(Box)<{ winner: string }>`
  position: absolute;
  height: 100%;
  background: ${({ winner }) =>
    winner === "CounterTerrorists" || winner === "Counter Terrorists"
      ? "rgba(113, 170, 255, 0.1)"
      : "rgba(249, 83, 112, 0.1)"};
  border-left: 2px solid
    ${({ winner }) =>
      winner === "CounterTerrorists" || winner === "Counter Terrorists"
        ? "#71AAFF"
        : "#F95370"};
`;

const RoundLabel = styled(Flex)`
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(247, 252, 253, 0.7);
`;

const RoundScore = styled.span`
  font-size: 10px;
  font-weight: 500;

  .ct {
    color: #71aaff;
  }
  .t {
    color: #f95370;
  }
`;

const PlayersContainer = styled(Box)``;

const PlayerRow = styled(Flex)<{ selected: boolean }>`
  position: relative;
  height: 56px;
  margin: 4px 0;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(113, 170, 255, 0.05);
  }

  ${({ selected }) =>
    selected &&
    css`
      background: rgba(113, 170, 255, 0.1);
    `}
`;

const PlayerInfo = styled(Flex)<{ selected: boolean }>`
  position: sticky;
  left: 0;
  z-index: 100;
  align-items: center;
  gap: 10px;
  width: 200px;
  min-width: 200px;
  padding: 8px 12px;
  background: ${({ selected }) => (selected ? "#1a2933" : "#131d25")};
  border-right: 1px solid #1e2e3a;
  border-left: 3px solid
    ${({ selected }) => (selected ? "#71AAFF" : "transparent")};
`;

const PlayerAvatar = styled(Flex)<{ selected: boolean }>`
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ selected }) => (selected ? "#71AAFF" : "#253a49")};
  color: ${({ selected }) => (selected ? "#0b1116" : "#8fa9af")};
`;

const PlayerDetails = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled(Text)<{ selected: boolean }>`
  font-size: 13px;
  font-weight: ${({ selected }) => (selected ? 600 : 500)};
  color: ${({ selected }) => (selected ? "#f7fcfd" : "#8fa9af")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerStats = styled(Flex)`
  gap: 8px;
  margin-top: 2px;
`;

const StatBadge = styled(Flex)<{ type: "kills" | "deaths" }>`
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: ${({ type }) => (type === "kills" ? "#4dbd94" : "#f95370")};
`;

const PlayerTimeline = styled(Box)<{ selected: boolean }>`
  position: relative;
  flex: 1;
  height: 100%;
  cursor: ${({ selected }) => (selected ? "crosshair" : "pointer")};
`;

const RoundSegment = styled(Box)<{
  team: "ct" | "t" | null;
  selected: boolean;
}>`
  position: absolute;
  top: 8px;
  height: calc(100% - 16px);
  background: ${({ team }) =>
    team === "ct"
      ? "rgba(113, 170, 255, 0.08)"
      : team === "t"
      ? "rgba(249, 83, 112, 0.08)"
      : "rgba(143, 169, 175, 0.05)"};
  border-left: 1px solid
    ${({ team }) =>
      team === "ct"
        ? "rgba(113, 170, 255, 0.2)"
        : team === "t"
        ? "rgba(249, 83, 112, 0.2)"
        : "rgba(143, 169, 175, 0.1)"};
  border-radius: 4px;
`;

const KillMarker = styled(Flex)<{ isHeadshot: boolean; selected: boolean }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  align-items: center;
  gap: 4px;
  padding: ${({ selected }) => (selected ? "4px 8px" : "4px")};
  background: ${({ isHeadshot }) => (isHeadshot ? "#FFD700" : "#4dbd94")};
  border-radius: ${({ selected }) => (selected ? "12px" : "50%")};
  color: #0b1116;
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
  }
`;

const KillLabel = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #0b1116;
  white-space: nowrap;
`;

const DeathMarker = styled(Flex)<{ selected: boolean }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: rgba(249, 83, 112, 0.3);
  border: 1px solid #f95370;
  border-radius: 50%;
  color: #f95370;
  opacity: ${({ selected }) => (selected ? 1 : 0.5)};
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.15);
    z-index: 10;
    opacity: 1;
    background: rgba(249, 83, 112, 0.5);
  }
`;

const SelectionOverlay = styled(Box)<{ isActive?: boolean }>`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  background: ${({ isActive }) =>
    isActive ? "rgba(77, 189, 148, 0.3)" : "rgba(77, 189, 148, 0.2)"};
  border: 2px solid ${({ isActive }) => (isActive ? "#5cd4a6" : "#4dbd94")};
  border-radius: 6px;
  z-index: 4;
`;

const SelectionHandle = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 24px;
  background: #4dbd94;
  border-radius: 3px;

  &.left {
    left: -3px;
  }
  &.right {
    right: -3px;
  }
`;

const SelectionsPanel = styled(Box)`
  width: 300px;
  background: #131d25;
  border-left: 1px solid #1e2e3a;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled(Box)`
  padding: 16px;
  border-bottom: 1px solid #1e2e3a;
`;

const Toast = styled(Box)`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #1a2933 0%, #253a49 100%);
  border: 1px solid rgba(113, 170, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  color: #f7fcfd;
  font-size: 13px;
  font-weight: 500;
  z-index: 1000;
  animation: slideUp 0.3s ease;

  svg {
    color: #71aaff;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const SelectionsList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #0b1116;
  }
  &::-webkit-scrollbar-thumb {
    background: #253a49;
    border-radius: 3px;
  }
`;

const EmptyState = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
`;

const EmptyIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #1a2933;
  border-radius: 50%;
  color: #557077;
`;

const SelectionCard = styled(Box)`
  padding: 12px;
  margin-bottom: 8px;
  background: #16232c;
  border: 1px solid #1e2e3a;
  border-radius: 8px;
  transition: all 0.15s ease;
  overflow: hidden;

  &:hover {
    border-color: #253a49;
  }
`;

const ClipHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const ClipContext = styled(Flex)`
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const ContextBadge = styled(Flex)<{ type?: "kills" | "deaths" | "duration" }>`
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: ${({ type }) =>
    type === "kills"
      ? "rgba(77, 189, 148, 0.15)"
      : type === "deaths"
      ? "rgba(249, 83, 112, 0.15)"
      : type === "duration"
      ? "rgba(113, 170, 255, 0.15)"
      : "rgba(143, 169, 175, 0.1)"};
  border-radius: 4px;
  font-size: 11px;
  color: ${({ type }) =>
    type === "kills"
      ? "#4dbd94"
      : type === "deaths"
      ? "#f95370"
      : type === "duration"
      ? "#71AAFF"
      : "#8fa9af"};
`;

const KillTimingSection = styled(Box)`
  margin-bottom: 10px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
`;

const KillTimingHeader = styled(Text)`
  font-size: 10px;
  color: #557077;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KillTimingRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const KillTimingBar = styled(Box)`
  position: relative;
  height: 4px;
  background: linear-gradient(
    to right,
    rgba(113, 170, 255, 0.3),
    rgba(77, 189, 148, 0.5),
    rgba(249, 83, 112, 0.3)
  );
  border-radius: 2px;
  margin-bottom: 4px;
`;

const KillTimingMarker = styled(Flex)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: #4dbd94;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: #0b1116;
`;

const DeathTimingBar = styled(Box)`
  position: relative;
  height: 4px;
  background: linear-gradient(
    to right,
    rgba(113, 170, 255, 0.3),
    rgba(249, 83, 112, 0.5),
    rgba(143, 169, 175, 0.3)
  );
  border-radius: 2px;
  margin-bottom: 4px;
`;

const DeathTimingMarker = styled(Flex)`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: #f95370;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: #0b1116;
`;

const KillDetailsRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const KillTimingInfo = styled(Box)`
  flex: 1;
  min-width: 0;
  font-size: 10px;

  .weapon {
    color: #8fa9af;
    display: block;
    margin-bottom: 2px;
  }

  .timing {
    color: #557077;
  }
`;

const PovToggle = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
`;

const PovOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.2)" : "transparent"};
  border: 1px solid ${({ active }) => (active ? "#71aaff" : "#253a49")};
  border-radius: 4px;
  font-size: 9px;
  color: ${({ active }) => (active ? "#71aaff" : "#557077")};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  max-width: 90px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: rgba(113, 170, 255, 0.1);
    border-color: #71aaff;
    color: #8fa9af;
  }
`;

const MountedCameraSection = styled(Box)`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #253a49;
`;

const MountedCameraHeader = styled(Flex)`
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #8fa9af;
  margin-bottom: 6px;

  svg {
    color: #71aaff;
  }
`;

const MountedCameraOptions = styled(Flex)`
  flex-wrap: wrap;
  gap: 4px;
`;

const MountedCameraOption = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: ${({ active }) =>
    active ? "rgba(113, 170, 255, 0.2)" : "rgba(30, 46, 58, 0.5)"};
  border: 1px solid ${({ active }) => (active ? "#71aaff" : "#253a49")};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  .label {
    font-size: 9px;
    font-weight: ${({ active }) => (active ? 600 : 400)};
    color: ${({ active }) => (active ? "#71aaff" : "#8fa9af")};
  }

  &:hover {
    background: rgba(113, 170, 255, 0.15);
    border-color: #71aaff;

    .label {
      color: #71aaff;
    }
  }
`;

const MountedCameraPreview = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
  padding: 6px 8px;
  background: linear-gradient(
    135deg,
    rgba(113, 170, 255, 0.1) 0%,
    rgba(77, 189, 148, 0.1) 100%
  );
  border: 1px solid rgba(113, 170, 255, 0.2);
  border-radius: 4px;

  .effect-label {
    font-size: 10px;
    color: #71aaff;
    font-weight: 500;
  }

  .duration {
    font-size: 9px;
    color: #557077;
  }
`;

const TimeAdjustRow = styled(Flex)`
  gap: 8px;
  width: 100%;
`;

const TimeAdjustGroup = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const TimeLabel = styled(Text)`
  font-size: 10px;
  color: #557077;
  margin-bottom: 4px;
  display: block;
`;

const TimeInputWrapper = styled(Flex)`
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
`;

const TimeIncrementBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 3px;
  color: #8fa9af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #253a49;
    color: #f7fcfd;
  }

  &:active:not(:disabled) {
    background: #71aaff;
    color: #0b1116;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const TimeInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  background: #0b1116;
  border: 1px solid #253a49;
  border-radius: 4px;
  color: #f7fcfd;
  font-size: 12px;
  font-family: monospace;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #71aaff;
  }
`;

const ClipNumber = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #4dbd94;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: #0b1116;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(249, 83, 112, 0.1);
  border: 1px solid rgba(249, 83, 112, 0.3);
  border-radius: 6px;
  color: #f95370;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.15s ease;

  &:hover {
    opacity: 1;
    background: rgba(249, 83, 112, 0.2);
  }
`;

const PanelFooter = styled(Box)`
  padding: 16px;
  border-top: 1px solid #1e2e3a;
  background: #0f171d;
`;

const ClipTitleSection = styled(Box)`
  margin-bottom: 12px;
`;

const ClipTitleLabel = styled(Text)`
  font-size: 12px;
  color: #557077;
  margin-bottom: 6px;
  display: block;
`;

const ClipTitleInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #0b1116;
  border: 1px solid #253a49;
  border-radius: 6px;
  color: #f7fcfd;
  font-size: 13px;
  box-sizing: border-box;
  transition: all 0.15s ease;

  &::placeholder {
    color: #557077;
  }

  &:focus {
    outline: none;
    border-color: #71aaff;
    background: #131d25;
  }

  &:hover:not(:focus) {
    border-color: #3a5066;
  }
`;

const TotalDuration = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 16px;
  background: linear-gradient(135deg, #4dbd94, #3a9d7a);
  border: none;
  border-radius: 8px;
  color: #0b1116;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5cd4a6, #4dbd94);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Tooltip = styled(Box)`
  position: fixed;
  background: #1a2933;
  border: 1px solid #253a49;
  border-radius: 8px;
  padding: 12px;
  z-index: 100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  max-width: 220px;
`;

const TooltipTitle = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: #f7fcfd;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #253a49;
`;

const TooltipRow = styled(Box)`
  font-size: 12px;
  color: #8fa9af;
  margin-bottom: 4px;

  span {
    color: #557077;
    margin-right: 4px;
  }
`;

const TooltipAction = styled(Text)`
  font-size: 11px;
  color: #4dbd94;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #253a49;
`;

const TimelineEditorPage = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { hasProEditAccess } = useProEditAccess();
  const { data, loading, error, submitReplaySequence, createClipLoading } =
    useTimelineEditorViewModel({ matchId: matchId || "" });

  if (!hasProEditAccess) {
    return <Redirect to="/match-history" />;
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Text fontSize="16px" color="#8fa9af">
          Loading match data...
        </Text>
      </LoadingContainer>
    );
  }

  if (error || !data) {
    return (
      <LoadingContainer>
        <Text fontSize="16px" color="#f95370">
          {error?.message || "Failed to load match data"}
        </Text>
      </LoadingContainer>
    );
  }

  return (
    <TimelineEditor
      data={data}
      onSubmitSequence={submitReplaySequence}
      isSubmitting={createClipLoading}
    />
  );
};

const LoadingContainer = styled(Flex)`
  height: 100vh;
  align-items: center;
  justify-content: center;
  background: #0b1116;
`;

export { TimelineEditor, TimelineEditorPage };
